import { Button } from '@velocity/ui';
import ls from 'localstorage-slim';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { fetchContracts } from '../../../api/instant-lease-api';
import isAuthenticated from '../../../redux/auth/authUtils';
import { setTextKeyVisibility } from '../../../redux/cache-slice';
import { Feature } from '../../../redux/feature-toggles/types';
import { openFeedbackForm } from '../../../redux/feedback/feedback-slice';
import { setContracts } from '../../../redux/leaseApp/lease-app-slice';
import { RootState } from '../../../redux/types';
import { AppStatus, Contracts } from '../../../types/instant-lease-api';
import { getAppStatus } from '../../../utils/getter';
import { errorLogging } from '../../../utils/logging';
import Navigation from '../../../utils/navigation';
import { ReactComponent as GlobeIcon } from '../../b2b/components/icons/globe.svg';
import { ReactComponent as TextIcon } from '../../b2b/components/icons/type.svg';
import { ReactComponent as CarIcon } from '../icons/car-button.svg';
import { ReactComponent as ChevronDown } from '../icons/chevron-down-icon.svg';
import { ReactComponent as ChevronUp } from '../icons/chevron-up-icon.svg';
import { ReactComponent as ContactIcon } from '../icons/contact-icon.svg';
import { ReactComponent as ExternalLink } from '../icons/external-link-icon.svg';
import { ReactComponent as FeedbackIcon } from '../icons/feedback-icon.svg';
import { ReactComponent as HelpIcon } from '../icons/help-icon.svg';
import { ReactComponent as LogoutIconWhite } from '../icons/logout-white.svg';
import { ReactComponent as UserFilledIcon } from '../icons/user-icon-filled.svg';
import { ReactComponent as UserIcon } from '../icons/user-icon.svg';
import SubHeader from '../sub-header/sub-header';
import './header.css';
import HeaderBar from './header-bar';
import { HamburgerIcon } from '@velocity/icons/system';

interface HeaderProps {
  showSubHeader?: boolean;
}

// Routes that render their own (logo-only) header via <HeaderBar />
const PAGES_WITHOUT_APP_HEADER = [
  'start',
  'login',
  'logout',
  'register',
  'reset-password',
  'invalid-offer-error',
  'contracts-history',
  'change-password',
];

// Figma: buttons/action-button-medium (40x40, 8px padding, 24px icon)
const actionButtonClass =
  'focus-style flex h-10 min-w-10 cursor-pointer items-center justify-center gap-1 rounded-[2px] p-2 text-(--vel-color-secondary-400,#0f3549)';

const Header: React.FC<HeaderProps> = ({ showSubHeader = true }) => {
  // Cache Store
  const { showTextKey, isSubheaderVisible } = useSelector(
    (state: RootState) => state.cache,
  );

  // Local env
  const isLocalDev = import.meta.env.MODE === 'development';

  // Sequence Store
  const { device } = useSelector((state: RootState) => state.sequence);

  // Auth State
  const { emailVerified, accessToken, expiresAt, email } = useSelector(
    (state: RootState) => state.auth,
  );

  // Lease Application Storage
  const { activeApplication, leaseApplications, contracts } =
    useSelector((state: RootState) => state.leaseApp);

  const { [Feature.Feedback]: feedback } = useSelector(
    (state: RootState) => state.featureToggles,
  );

  // Application status
  const status = getAppStatus(activeApplication);

  // dispatch hook
  const dispatch = useDispatch();

  // Sub-menu visibility
  const [subMenuVisibility, setSubMenuVisibility] = useState<boolean>(false);

  // Navigation
  const { nextRoute } = new Navigation();

  // Navigate Hook
  const navigate = useNavigate();

  // Current URL
  const url = useLocation().pathname;

  // Header visibility (pages in the list render their own logo-only header)
  const visible = !PAGES_WITHOUT_APP_HEADER.includes(url.split('/')[1]);

  const helpRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);
  const logoutRef = useRef<HTMLDivElement>(null);

  // Get previous contracts
  useEffect(() => {
    if (
      isAuthenticated({
        accessToken,
        expiresAt,
      }) &&
      contracts &&
      contracts?.length <= 0
    ) {
      fetchContracts(accessToken)
        .then(({ contracts }: Contracts) => {
          if (contracts?.length > 0) {
            dispatch(setContracts(contracts));
          }
        })
        .catch((error) => errorLogging(new Error(`${error}`)));
    }
  }, [accessToken, contracts, dispatch, expiresAt]);

  // Disable scroll in case of desktop
  useEffect(() => {
    if (
      device === 'desktop' &&
      document.body.classList.contains('scroll-disable')
    ) {
      document.body.classList.remove('scroll-disable');
    }
  }, [device]);

  useEffect(() => {
    if (subMenuVisibility) {
      const timeout = setTimeout(() => {
        helpRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [subMenuVisibility]);

  const getUserFullName = (): string => {
    const person =
      leaseApplications[0]?.created_by?.person ||
      activeApplication?.created_by?.person;
    const first = ls.get('first_name') || person?.first_name;
    const last = ls.get('last_name') || person?.last_name;
    return first && last ? `${first} ${last}` : '';
  };

  const toggleSubHeader = (forceState?: boolean) => {
    const isMobile = device === 'mobile';
    const bodyHasClass = document.body.classList.contains('scroll-disable');
    if (isMobile)
      document.body.classList.toggle('scroll-disable', !bodyHasClass);
    setSubMenuVisibility(forceState ?? !subMenuVisibility);
  };

  if (!visible) return null;
  // In the case of failed, credit failed or esignature failed, we want to show the header without the subheader and without the user menu
  if (
    status === AppStatus.Failed ||
    status === AppStatus.CreditFailed ||
    status === AppStatus.EsignatureFailed ||
    status === AppStatus.Timedout ||
    status === undefined
  ) {
    showSubHeader = false;
  }

  const userFullName = getUserFullName();

  return (
      <div
      className={`app-header relative z-30 ${
        status === AppStatus.Cancelled ? 'is-cancelled' : ''
      } ${isSubheaderVisible ? 'is-subheader-open' : ''}`.trim()}
      data-testid='header-container'
    >
      <HeaderBar>
        {accessToken && (
          <>
            {/* Tablet / desktop: user toggle opens the user menu popup */}
            <button
              type='button'
              className={`user-toggle ${actionButtonClass} max-md:hidden!`}
              aria-label='Benutzermenü öffnen oder schließen'
              aria-expanded={subMenuVisibility}
              data-testid='user-menu-toggle'
              onClick={() => toggleSubHeader()}
            >
              {subMenuVisibility ? (
                <UserFilledIcon
                  className='header-icons shrink-0'
                  width='24px'
                  height='24px'
                  aria-hidden='true'
                />
              ) : (
                <UserIcon
                  className='header-icons shrink-0'
                  width='24px'
                  height='24px'
                  aria-hidden='true'
                />
              )}
              {userFullName && (
                <span
                  className={`user-name ${
                    subMenuVisibility ? 'text-tight-200-bold' : 'text-tight-200'
                  } whitespace-nowrap text-(--vel-color-secondary-400,#0f3549)!`}
      >
                  {userFullName}
                </span>
              )}
              {subMenuVisibility ? (
                <ChevronUp
                  className='arrow green header-icons shrink-0'
                  aria-hidden='true'
                />
              ) : (
                <ChevronDown
                  className='arrow header-icons shrink-0'
                  aria-hidden='true'
                />
              )}
            </button>

            {/* Mobile only: hamburger opens the full-screen user menu */}
            <button
              type='button'
              className={`header-menu-button ${actionButtonClass} md:hidden!`}
              aria-label='Menü öffnen oder schließen'
              aria-expanded={subMenuVisibility}
              data-testid='header-menu-button'
              onClick={() => toggleSubHeader()}
            >
              <HamburgerIcon aria-hidden='true' size={'s'} />
            </button>
          </>
        )}
      </HeaderBar>

            {subMenuVisibility && (
              <div className='menu-header' data-testid='user-menu'>
                <div className='menu-header-body'>
                  <h3 className='content-bold'>Hallo</h3>
                  <p className='content-normal'>{userFullName}</p>
                  <p className='dark-grey content-normal'>
                    {email}
                    {!emailVerified && (
                      <>
                        {' '}
                        | <span className='light-grey'>Nicht verifiziert</span>
                      </>
                    )}
                  </p>{' '}
                  {contracts && contracts?.length > 0 && (
                    <>
                      <div
                        className='header-section focus-style'
                        role='button'
                        tabIndex={0}
                        aria-label='Meine abgeschlossenen Verträge'
                        onClick={() =>
                          window.open('/contracts-history', '_blank')
                        }
                        onKeyDown={(e) =>
                          ['Enter', ' '].includes(e.key) &&
                          window.open('/contracts-history', '_blank')
                        }
                      >
                        <CarIcon />
                        <span className='dark-grey content-normal'>
                          Meine abgeschlossenen Verträge
                        </span>
                        <ExternalLink
                          className='link-icon'
                          width='20px'
                          height='20px'
                        />
                      </div>
                    </>
                  )}
                  <div
                    ref={helpRef}
                    className='header-section focus-style'
                    role='button'
                    tabIndex={0}
                    aria-label='Hilfe und FAQ öffnen'
                    onClick={() =>
                      window.open('https://www.ald-online.de/faq', '_blank')
                    }
                    onKeyDown={(e) =>
                      ['Enter', ' '].includes(e.key) &&
                      window.open('https://www.ald-online.de/faq', '_blank')
                    }
                  >
                    <HelpIcon />
                    <span className='dark-grey content-normal'>
                      Hilfe | FAQ
                    </span>
                    <ExternalLink
                      className='link-icon'
                      width='20px'
                      height='20px'
                    />
                  </div>
                  <div
                    ref={contactRef}
                    className='header-section focus-style'
                    role='button'
                    tabIndex={0}
                    aria-label='Kontakt öffnen'
                    onClick={() =>
                      window.open(
                        'https://www.ald-online.de/pages/kontaktiere-uns',
                        '_blank',
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        window.open(
                          'https://www.ald-online.de/pages/kontaktiere-uns',
                          '_blank',
                        );
                      } else if (e.key === 'Tab' && !e.shiftKey) {
                        e.preventDefault();
                        setSubMenuVisibility(false);
                        setTimeout(() => logoutRef.current?.focus(), 50);
                      }
                    }}
                  >
                    <ContactIcon />
                    <span className='dark-grey content-normal'>Kontakt</span>
                    <ExternalLink
                      className='link-icon'
                      width='20px'
                      height='20px'
                    />
                  </div>
                  {feedback.enabled && (
                    <div
                      className='header-section feedback'
                      style={{ cursor: 'pointer' }}
                      onClick={() => dispatch(openFeedbackForm())}
                    >
                      <FeedbackIcon />
                      <span className='content-bold'>
                        Gib uns dein Feedback
                      </span>
                    </div>
                  )}
                  <div
              ref={logoutRef}
              className='header-section logout-section focus-style'
              role='button'
              tabIndex={0}
              aria-label='Abmelden'
              data-testid='logout-button'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      window.location.href = '/logout';
                    }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  window.location.href = '/logout';
                }
              }}
                  >
              <LogoutIconWhite aria-hidden='true' />
                    <span className='content-bold'>Jetzt abmelden</span>
                  </div>
                </div>
                <Button
            variant='secondary-filled'
            size='l'
            className='submenu-close md:hidden!'
                  onClick={() => toggleSubHeader(false)}
                >
                  Menü schließen
                </Button>
              </div>
            )}

          {showSubHeader && <SubHeader />}
          {isLocalDev && (
            <>
              <div className='float-button'>
                {showTextKey ? (
                  <TextIcon
                    width='25px'
                    height='25px'
                    onClick={() => dispatch(setTextKeyVisibility(!showTextKey))}
                  />
                ) : (
                  <GlobeIcon
                    width='25px'
                    height='25px'
                    onClick={() => dispatch(setTextKeyVisibility(!showTextKey))}
                  />
                )}
              </div>
              <Button
            variant='primary-filled'
            size='m'
                className='pass-button'
                onClick={() => navigate(nextRoute())}
              >
                Pass
              </Button>
            </>
          )}
        </div>
  );
};

export default Header;
